import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { formatError, api } from '@/lib/api';
import { logger } from '@/lib/logger';
import { Scales, Eye, EyeSlash, Warning } from '@phosphor-icons/react';

export default function RegisterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailWarning, setEmailWarning] = useState('');
  const [emailSuggestion, setEmailSuggestion] = useState('');
  const { register } = useAuth();
  const navigate = useNavigate();

  const validateEmail = async (val) => {
    if (!val || val.length < 5) { setEmailWarning(''); setEmailSuggestion(''); return; }
    try {
      const { data } = await api.post('/auth/validate-email', { email: val });
      if (!data.valid) {
        setEmailWarning(data.error || 'Invalid email');
        setEmailSuggestion(data.suggestion || '');
      } else {
        setEmailWarning('');
        setEmailSuggestion('');
      }
    } catch (err) {
      // Email validation is non-critical; warn quietly
      logger.warn('Email validation skipped:', err?.message || err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!name.trim()) { setError('Name is required'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      await register(email, password, name);
      navigate('/dashboard');
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="register-page">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img src="https://images.unsplash.com/photo-1733951433050-e29718fd08f4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxhcmNoaXRlY3R1cmFsJTIwZ2VvbWV0cmljJTIwbGluZXMlMjBiYWNrJTIwYW5kJTIwd2hpdGV8ZW58MHx8fHwxNzc2MDQ1MTY3fDA&ixlib=rb-4.1.0&q=85"
          alt="ClarityCase AI" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#002FA7]/80" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <h2 className="font-outfit text-4xl font-bold mb-3">ClarityCase AI</h2>
          <p className="text-lg text-white/80 font-ibm max-w-md">Join thousands of investigators building airtight cases with AI-powered document analysis.</p>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-8 bg-[#F9FAFB]">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2 mb-8">
            <Scales weight="duotone" className="w-8 h-8 text-[#002FA7]" />
            <span className="font-outfit text-2xl font-bold text-[#09090B]">ClarityCase AI</span>
          </div>
          <h1 className="font-outfit text-3xl font-bold text-[#09090B] mb-1">Create account</h1>
          <p className="text-[#6B7280] font-ibm mb-8">Start building your investigation in minutes</p>
          {error && <div data-testid="register-error" className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm font-ibm">{error}</div>}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-xs font-bold tracking-[0.2em] uppercase text-[#6B7280] mb-2 font-ibm">Full Name</label>
              <input data-testid="register-name-input" type="text" value={name} onChange={e => setName(e.target.value)} required
                className="w-full px-4 py-3 border border-[#E5E7EB] rounded-md bg-white text-[#09090B] font-ibm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent transition-all" placeholder="John Doe" />
            </div>
            <div>
              <label className="block text-xs font-bold tracking-[0.2em] uppercase text-[#6B7280] mb-2 font-ibm">Email</label>
              <input data-testid="register-email-input" type="email" value={email}
                onChange={e => { setEmail(e.target.value); setEmailWarning(''); setEmailSuggestion(''); }}
                onBlur={() => validateEmail(email)}
                required
                className={`w-full px-4 py-3 border rounded-md bg-white text-[#09090B] font-ibm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent transition-all ${emailWarning ? 'border-orange-300' : 'border-[#E5E7EB]'}`} placeholder="you@example.com" />
              {emailWarning && (
                <div className="mt-1.5 flex items-start gap-2">
                  <Warning size={14} className="text-orange-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs text-orange-600 font-ibm" data-testid="email-warning">{emailWarning}</p>
                    {emailSuggestion && (
                      <button type="button" onClick={() => { setEmail(emailSuggestion); setEmailWarning(''); setEmailSuggestion(''); }} data-testid="email-suggestion-btn"
                        className="text-xs text-[#002FA7] font-ibm font-semibold hover:underline mt-0.5">
                        Use {emailSuggestion}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div>
              <label className="block text-xs font-bold tracking-[0.2em] uppercase text-[#6B7280] mb-2 font-ibm">Password</label>
              <div className="relative">
                <input data-testid="register-password-input" type={showPw ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} required
                  className="w-full px-4 py-3 border border-[#E5E7EB] rounded-md bg-white text-[#09090B] font-ibm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent transition-all pr-12" placeholder="Min 6 characters" />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7280] hover:text-[#09090B]">
                  {showPw ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
            <button data-testid="register-submit-button" type="submit" disabled={loading}
              className="w-full py-3 bg-[#002FA7] text-white font-ibm font-semibold rounded-md hover:bg-[#00237D] active:scale-[0.98] transition-all disabled:opacity-50">
              {loading ? 'Creating account...' : 'Create Account'}
            </button>
          </form>
          <p className="mt-6 text-center text-sm text-[#6B7280] font-ibm">
            Already have an account? <Link to="/login" className="text-[#002FA7] font-semibold hover:underline" data-testid="login-link">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
